import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export const exportToPDF = async (
  element: HTMLElement,
  filename: string,
  orientation: 'portrait' | 'landscape' = 'portrait'
): Promise<void> => {
  try {
    // Validate element
    if (!element) {
      throw new Error('Element not found');
    }

    // Create canvas from HTML element
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight
    });

    // Validate canvas dimensions
    if (!canvas || canvas.width === 0 || canvas.height === 0) {
      throw new Error('Invalid canvas dimensions. Element may be hidden or not rendered.');
    }

    const imgData = canvas.toDataURL('image/png');

    // Validate image data
    if (!imgData || imgData === 'data:,') {
      throw new Error('Failed to generate image data from canvas');
    }

    // Create PDF
    const pdf = new jsPDF({
      orientation: orientation,
      unit: 'mm',
      format: 'a4'
    });

    // Calculate image dimensions to fit the page
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Calculate ratios with safety checks
    const widthRatio = pageWidth / canvas.width;
    const heightRatio = pageHeight / canvas.height;
    const ratio = Math.min(widthRatio, heightRatio);

    // Validate ratio
    if (!isFinite(ratio) || ratio <= 0) {
      throw new Error('Invalid scaling ratio calculated');
    }

    const canvasWidth = canvas.width * ratio;
    const canvasHeight = canvas.height * ratio;

    // Center the image on the page
    const marginX = Math.max(0, (pageWidth - canvasWidth) / 2);
    const marginY = 10; // Top margin

    // Validate all parameters before adding image
    if (!isFinite(marginX) || !isFinite(marginY) || !isFinite(canvasWidth) || !isFinite(canvasHeight)) {
      throw new Error('Invalid coordinates or dimensions calculated');
    }

    if (canvasWidth <= 0 || canvasHeight <= 0) {
      throw new Error('Calculated dimensions are invalid');
    }

    // Add image to PDF
    pdf.addImage(imgData, 'PNG', marginX, marginY, canvasWidth, canvasHeight);

    // Save PDF
    pdf.save(filename);
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error(`Failed to generate PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const printElement = (element: HTMLElement): void => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    throw new Error('Could not open print window');
  }

  const styles = Array.from(document.styleSheets)
    .map(styleSheet => {
      try {
        return Array.from(styleSheet.cssRules)
          .map(rule => rule.cssText)
          .join('\n');
      } catch (e) {
        return '';
      }
    })
    .join('\n');

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Impression</title>
        <style>
          ${styles}
          @media print {
            @page {
              size: A4;
              margin: 15mm;
            }
            body {
              margin: 0;
              padding: 0;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
        </style>
      </head>
      <body>
        ${element.innerHTML}
      </body>
    </html>
  `);

  printWindow.document.close();

  // Wait for images to load before printing
  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }, 500);
  };
};
